"use client";

import Link from "next/link";
import { useParams } from "next/navigation";
import { FaInstagram, FaWhatsapp } from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { TfiLocationPin } from "react-icons/tfi";
import FeedbackBanner from "../FeedbackBanner/FeedbackBanner";
import { footerTexts } from "@/lib/sharedTranslations";

export default function Footer() {
  const params = useParams();
  const locale = (params?.locale as "pt" | "en") ?? "pt";
  const t = footerTexts[locale] ?? footerTexts["pt"];

  return (
    <footer className="bg-[#1B475D] text-gray-200 text-sm">
      <div className="bg-[#1B475D] max-w-7xl mx-auto">
        <FeedbackBanner />
      </div>

      <div className="bg-[#1B475D] max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10 justify-center">
        {/* Column 1: Branding */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#F1F5F9] mb-3">Genau Hub</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center text-[#F1F5F9] justify-center gap-2">
              <TfiLocationPin className="text-[#F1F5F9]" />
              <span>{t.location}</span>
            </div>

            <div className="flex items-center text-[#F1F5F9] justify-center gap-2">
              <FaWhatsapp className="text-green-400" />
              <a
                href="https://wa.me/4917688148825"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hover:underline"
              >
                {t.whatsapp}
              </a>
            </div>

            <div className="flex items-center text-[#F1F5F9] justify-center gap-2 mb-2">
              <FaInstagram className="text-[#8EBD9D]" />
              <a
                href="https://www.instagram.com/genauhub"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hover:underline"
              >
                {t.instagram}
              </a>
            </div>

            <div className="flex items-center text-[#F1F5F9] justify-center gap-2">
              <FiMail className="text-[#F1F5F9]" />
              <a href={`mailto:${t.email}`} className="hover:underline">
                {t.email}
              </a>
            </div>
          </div>
        </div>

        {/* Column 2: About */}
        <div className="text-center text-[#F1F5F9]">
          <h3 className="font-semibold mb-3">{t.sections.genauhub}</h3>
          <ul className="space-y-2">
            <li>
              <Link href={`/${locale}/about`}>{t.sections.about}</Link>
            </li>
            <li>
              <Link href={`/${locale}/services`}>{t.sections.services}</Link>
            </li>
            <li>
              <Link href={`/${locale}/professionals`} className="hover:underline">
                {t.sections.professionals}
              </Link>
            </li>
            <li>
              <Link href={`/${locale}/community`}>{t.sections.community}</Link>
            </li>
          </ul>
        </div>

        {/* Column 3: Membership */}
        <div className="text-center text-[#F1F5F9]">
          <h3 className="font-semibold mb-3">{t.sections.membership}</h3>
          <ul className="space-y-2">
            <li>
              <Link href={`/${locale}/register`}>{t.sections.register}</Link>
            </li>
          </ul>
        </div>

        {/* Column 4: Explore */}
        <div className="text-center text-[#F1F5F9]">
          <h3 className="font-semibold mb-3">{t.sections.explore}</h3>
          <ul className="space-y-2">
            <li>
              <Link href={`/${locale}/places`}>{t.sections.localPlaces}</Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#1B475D] mx-auto max-w-7xl border-t border-[#8EBD9D] text-center py-4 px-4 text-xs text-[#F1F5F9]">
        © Genau Hub. {t.copyright}
        &nbsp;|&nbsp;
        <Link href={`/${locale}/nutzungsbedingungen`} className="underline">
          {t.terms}
        </Link>
        &nbsp;|&nbsp;
        <Link href={`/${locale}/impressum`} className="underline">
          {t.imprint}
        </Link>
        &nbsp;|&nbsp;
        <Link href={`/${locale}/datenschutz`} className="underline">
          {t.privacy}
        </Link>
      </div>
    </footer>
  );
}
